import React, { useState, useEffect, useMemo } from 'react';
import { InventoryItem, SilverItem, WatchItem, ItemType, ItemStatus, Currency } from '../../types';
import { InfoField, InfoGroup, DetailModal, DropdownMenu } from '../ui';
import { FormField, TextInput, NumberInput, SelectInput } from '../ui';
import { useCategory } from '../../context/CategoryContext';
import { useInventory } from '../../context/InventoryContext';
import { useSwipeable } from 'react-swipeable';
import { Button } from '../ui';
import { ButtonVariant, ButtonSize } from '../../types/ui';
import Modal from '../ui/Modal';
import { NotificationType } from '../../types/ui';
import { markItemAsSold } from '../../services/inventoryService';
import { formatCurrency, getCurrentExchangeRate } from '../../services/exchangeRateService';
import { formatItemPrice } from '../../utils/currencyUtils';
import { BarcodeButton } from '../barcode';
import { useResponsive, useBarcodeRegistration } from '../../hooks';
import { BarcodeInputTargetType } from '../../services/barcodeService';
import ReactDOM from 'react-dom';
import { FormModal } from '../ui';

// Form context ID for the edit mode
const ITEM_DETAIL_EDIT_FORM_ID = 'item-detail-edit-form';

// Replace tooltipStyle and tooltipTextStyle with a Tailwind-based Tooltip component
const showTooltip = (e: React.MouseEvent<HTMLSpanElement>) => {
  const tooltip = e.currentTarget.querySelector('.tooltiptext') as HTMLElement;
  if (tooltip) {
    tooltip.style.visibility = 'visible';
    tooltip.style.opacity = '1';
  }
};

const hideTooltip = (e: React.MouseEvent<HTMLSpanElement>) => {
  const tooltip = e.currentTarget.querySelector('.tooltiptext') as HTMLElement;
  if (tooltip) {
    tooltip.style.visibility = 'hidden';
    tooltip.style.opacity = '0';
  }
};

// Tooltip component
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <span 
    className="relative inline-block cursor-help border-b border-dotted border-gray-600"
    onMouseEnter={showTooltip}
    onMouseLeave={hideTooltip}
  >
    {children}
    <span className="tooltiptext invisible opacity-0 w-[200px] bg-gray-700 text-white text-center rounded-md py-1 px-2 absolute z-10 bottom-full left-1/2 -ml-[100px] transition-opacity duration-300">{text}</span>
  </span>
);

// Formatting utilities
const formatWeight = (weight: number | undefined): string => {
  if (weight === undefined) return '-';
  return `${weight.toFixed(2)} gr`;
};

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format date to YYYY-MM-DD for input
const formatDateForInput = (date: Date | undefined): string => {
  if (!date) return '';
  return date instanceof Date ? date.toISOString().split('T')[0] : '';
};

// Format price with Turkish Lira symbol
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);
};

// Currency options for select inputs
const currencyOptions = [
  { value: Currency.TRY, label: 'TRY (₺)' },
  { value: Currency.USD, label: 'USD ($)' }
];

type ViewMode = 'customer' | 'details' | 'edit';

interface ItemDetailModalProps {
  item: InventoryItem;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (item: InventoryItem) => void;
  onDelete?: (itemId: string) => void;
  isBarcodeScan?: boolean;
  showNotification?: (type: NotificationType, message: string) => void;
}

// Type guard for SilverItem to verify type before accessing properties
const isSilverItemWithCost = (item: InventoryItem): boolean => {
  return item.type === ItemType.SILVER && 
         'costPerGram' in item && 
         item.costPerGram !== undefined && 
         item.costPerGram > 0;
};

/**
 * Universal item detail modal component that works for both regular inventory view
 * and barcode scanning workflow.
 */
const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ 
  item, 
  isOpen, 
  onClose,
  onEdit,
  onDelete,
  isBarcodeScan = false,
  showNotification
}) => {
  // For barcode scan, we start with the customer view, otherwise details view
  const [viewMode, setViewMode] = useState<ViewMode>(isBarcodeScan ? 'customer' : 'details');
  const isSilverItem = item.type === ItemType.SILVER;
  const isWatchItem = item.type === ItemType.WATCH;
  const { categories } = useCategory();
  const { modifyItem, removeItem } = useInventory();
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  const [categoryName, setCategoryName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Exchange rate state for display
  const [tryEquivalent, setTryEquivalent] = useState<number>(0);
  const [currentExchangeRate, setCurrentExchangeRate] = useState<number>(0);

  // State to track pricing mode when editing silver items
  const [pricingMode, setPricingMode] = useState<'perUnit' | 'perGram'>(
    item.type === ItemType.SILVER ? 'perGram' : 'perUnit'
  );

  // Form state for editing
  const [formData, setFormData] = useState<Partial<SilverItem | WatchItem>>({
    ...item
  });

  const [isSellModalOpen, setIsSellModalOpen] = useState(false);
  const [isSubmittingSell, setIsSubmittingSell] = useState(false);
  const [sellPrice, setSellPrice] = useState<number | undefined>(undefined);
  const [sellCurrency, setSellCurrency] = useState<Currency>(Currency.USD);

  // Register with barcode service for edit mode using the standardized hook
  useBarcodeRegistration({
    formId: ITEM_DETAIL_EDIT_FORM_ID,
    formFields: [{ id: 'barcode', type: BarcodeInputTargetType.FORM_FIELD }],
    isActive: isOpen && viewMode === 'edit'
  });

  // Swipe handlers for mobile (used for all workflows now)
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (viewMode === 'customer') setViewMode('details');
      else if (viewMode === 'details') setViewMode('edit');
    },
    onSwipedRight: () => {
      if (viewMode === 'edit') setViewMode('details');
      else if (viewMode === 'details') setViewMode('customer');
    },
    trackMouse: true
  });

  // Get mobile-only swipe handlers, or empty object for desktop
  const mobileSwipeHandlers = isMobile ? swipeHandlers : {};

  // Find the category name based on the ID when item or categories change
  useEffect(() => {
    if (categories.length > 0 && item.category) {
      const category = categories.find(cat => cat.id === item.category);
      setCategoryName(category ? category.name : item.category);
    }
  }, [categories, item.category]);

  // Memoized check if item is already sold
  const isItemSold = useMemo(() => item.status === ItemStatus.SOLD, [item.status]);

  // Calculate TRY equivalent and get current exchange rate when needed
  useEffect(() => {
    const updateExchangeRateInfo = async () => {
      if (item.sellCurrency === Currency.USD) {
        const rate = await getCurrentExchangeRate();
        setCurrentExchangeRate(rate);

        // Use stored rate for sold items, current rate for unsold items
        if (isItemSold && item.soldExchangeRateToTry) {
          setTryEquivalent(item.sellPerUnit * item.soldExchangeRateToTry);
        } else {
          setTryEquivalent(item.sellPerUnit * rate);
        }
      }
    };

    updateExchangeRateInfo();
  }, [item, isItemSold]);

  // Helper functions for silver item calculations
  const calculatePerGramValues = (silverData: Partial<SilverItem>, weight: number): { 
    pricePerGram: number, 
    costPerGram: number 
  } => {
    const pricePerGram = (silverData.sellPerUnit || 0) / weight;
    const costPerGram = (silverData.costPerUnit || 0) > 0 ? (silverData.costPerUnit || 0) / weight : 0;
    return { pricePerGram, costPerGram };
  };

  const calculatePerUnitValues = (silverData: Partial<SilverItem>, weight: number): {
    sellPerUnit: number,
    costPerUnit: number
  } => {
    const sellPerUnit = (silverData.pricePerGram || 0) * weight;
    const costPerUnit = silverData.costPerGram ? silverData.costPerGram * weight : 0;
    return { sellPerUnit, costPerUnit };
  };

  // Calculate derived values for silver items based on pricing mode
  useEffect(() => {
    if (!isOpen || item.type !== ItemType.SILVER || viewMode !== 'edit') {
      return;
    }

    const silverData = formData as Partial<SilverItem>;
    const weight = silverData.weight || 0;

    // Skip updates if we don't have meaningful values to work with
    if (weight <= 0) {
      return;
    }

    // Calculate new values based on pricing mode
    let newValues: Partial<SilverItem> = {};
    let shouldUpdate = false;

    if (pricingMode === 'perGram' && silverData.pricePerGram !== undefined) {
      const { sellPerUnit, costPerUnit } = calculatePerUnitValues(silverData, weight);

      // Only update if values are different
      if (sellPerUnit !== silverData.sellPerUnit || costPerUnit !== silverData.costPerUnit) {
        newValues = { sellPerUnit, costPerUnit };
        shouldUpdate = true;
      }
    } else if (pricingMode === 'perUnit') {
      const { pricePerGram, costPerGram } = calculatePerGramValues(silverData, weight);

      // Only update if values are different
      if (pricePerGram !== silverData.pricePerGram || costPerGram !== silverData.costPerGram) {
        newValues = { pricePerGram, costPerGram };
        shouldUpdate = true;
      }
    }

    // Only update state if needed
    if (shouldUpdate) {
      setFormData(prev => ({
        ...prev,
        ...newValues
      }));
    }
  }, [pricingMode, item.type, viewMode, isOpen, formData]);

  // Reset sell price state when item changes
  useEffect(() => {
    if (item) {
      setSellPrice(item.sellPerUnit);
    }
  }, [item]);

  // Reset form data when item changes
  useEffect(() => {
    // Reset all local state based on the item prop
    setFormData({...item});
    setPricingMode(item.type === ItemType.SILVER ? 'perGram' : 'perUnit');
    setSellCurrency(item.sellCurrency);
    setSellPrice(item.sellPerUnit);

    // Reset view state if needed when item changes
    if (isBarcodeScan) {
      setViewMode('customer');
    }

    // Show appropriate view mode based on item status
    const newViewMode = viewMode;
    setViewMode(newViewMode);
  }, [item, isBarcodeScan]);

  // Commonly reused utility functions
  const handleBarcodeButtonClick = () => {
    setTimeout(() => {
      const barcodeBtn = document.querySelector('.barcode-print-btn') as HTMLButtonElement;
      if (barcodeBtn) {
        barcodeBtn.click();
      }
    }, 0);
  };

  // Get action button configuration based on current view mode
  const getActionButtons = () => {
    const buttons: {
      text: string;
      onClick: () => void;
      variant?: 'primary' | 'secondary' | 'danger';
      isMain?: boolean;
      render?: () => React.ReactNode;
    }[] = [];

    const dropdownActions: {
      label: string;
      onClick: () => void;
    }[] = [];

    const navigationInfo = {
      prevView: null as ViewMode | null,
      nextView: null as ViewMode | null
    };

    // Set up navigation flow based on current view
    if (viewMode === 'customer') {
      navigationInfo.nextView = 'details';
    } else if (viewMode === 'details') {
      navigationInfo.prevView = 'customer';
      navigationInfo.nextView = 'edit';
    } else if (viewMode === 'edit') {
      navigationInfo.prevView = 'details';
    }

    // Common buttons for all views
    if (viewMode === 'customer') {
      // Add "Detayları Görüntüle" to dropdown
      dropdownActions.push({
        label: 'Detayları Görüntüle',
        onClick: () => setViewMode('details')
      });

      // Add "Barkod Yazdır" to dropdown
      dropdownActions.push({
        label: 'Barkod Yazdır',
        onClick: handleBarcodeButtonClick
      });

      // Keep "Satış Yap" as main button
      if (!isItemSold) {
        buttons.push({
          text: 'Satış Yap',
          onClick: handleSellClick,
          variant: 'primary'
        });
      }
    } else if (viewMode === 'details') {
      // Add "Fiyat Ekranı" to dropdown
      if (isBarcodeScan || !isBarcodeScan) {
        dropdownActions.push({
          label: 'Fiyat Ekranı',
          onClick: () => setViewMode('customer')
        });
      }

      // Add "Barkod Yazdır" to dropdown
      dropdownActions.push({
        label: 'Barkod Yazdır',
        onClick: handleBarcodeButtonClick
      });

      // Remove "Satış Yap" button from details view

      // Add "Düzenle" to dropdown only if not sold
      if (!isItemSold) {
        dropdownActions.push({
          label: 'Düzenle',
          onClick: () => setViewMode('edit')
        });
      }

      // Add delete button to dropdown if onDelete is provided
      if (onDelete) {
        dropdownActions.push({
          label: 'Ürünü Sil',
          onClick: handleDelete
        });
      }
    } else if (viewMode === 'edit') {
      // Edit mode buttons
      buttons.push({
        text: 'İptal',
        onClick: () => setViewMode('details'),
        variant: 'secondary'
      });

      // Create a wrapper function that doesn't take an event parameter
      const handleSubmitClick = () => {
        handleSubmit(new Event('submit') as unknown as React.FormEvent);
      };

      buttons.push({
        text: isSubmitting ? 'Güncelleniyor...' : 'Güncelle',
        onClick: handleSubmitClick,
        variant: 'primary'
      });
    }

    return { buttons, dropdownActions, navigationInfo };
  };

  // If not open, don't render anything
  if (!isOpen) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    // Handle number inputs
    if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : Number(value)
      }));
    } else if (name === 'costCurrency' || name === 'sellCurrency') {
      // Handle currency selection
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'purchaseDate') {
      // Handle date input
      const date = value ? new Date(value) : undefined;
      setFormData(prev => ({
        ...prev,
        [name]: date
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare final data for submission
      const fieldsToExclude = ['soldAt', 'id', 'createdAt', 'updatedAt', 'type'];
      const submissionData = Object.entries(formData)
        .filter(([key, value]) => !fieldsToExclude.includes(key) && value !== undefined)
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

      // Ensure all required derived values are included for silver items
      if (item.type === ItemType.SILVER) {
        const silverData = submissionData as Partial<SilverItem>;
        const weight = silverData.weight || 0;

        if (weight > 0) {
          if (pricingMode === 'perGram') {
            // Use extracted helper function for consistency
            const { sellPerUnit, costPerUnit } = calculatePerUnitValues(silverData, weight);
            silverData.sellPerUnit = sellPerUnit;
            silverData.costPerUnit = costPerUnit;
          } else {
            // Use extracted helper function for consistency
            const { pricePerGram, costPerGram } = calculatePerGramValues(silverData, weight);
            silverData.pricePerGram = pricePerGram;
            silverData.costPerGram = costPerGram;
          }
        }
      }

      // Update the item in context
      await modifyItem(item.id, submissionData);

      // Change view mode after successful update
      setViewMode('details');

      if (showNotification) {
        showNotification(NotificationType.SUCCESS, 'Ürün başarıyla güncellendi');
      }
    } catch (error) {
      setError('Ürün güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');

      if (showNotification) {
        showNotification(NotificationType.ERROR, 'Ürün güncellenirken bir hata oluştu');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter categories based on item type
  const filteredCategories = categories.filter(category => category.typeId === item.type);

  // Convert categories to select options
  const categoryOptions = filteredCategories.map(category => ({
    value: category.id,
    label: category.name
  }));

  // Handle opening the sell confirmation modal
  const handleSellClick = () => {
    setSellPrice(item.sellPerUnit);
    setIsSellModalOpen(true);
  };

  // Handle price input change
  const handleSellPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSellPrice(value === '' ? undefined : Number(value));
  };

  // Handle selling the item
  const handleSellConfirm = async () => {
    setIsSubmittingSell(true);
    setError(null); // Clear previous errors specific to sell modal

    try {
      const saleOptions: { sellPerUnit?: number; sellCurrency?: Currency } = {};

      // Only include options if they have actually changed from the item's current state
      if (sellPrice !== undefined && sellPrice !== item.sellPerUnit) {
        saleOptions.sellPerUnit = sellPrice;
      }
      if (sellCurrency !== item.sellCurrency) {
        saleOptions.sellCurrency = sellCurrency;
      }

      // Call the service function to mark item as sold and create transaction.
      // The service function now handles item update to 'SOLD' and transaction creation.
      await markItemAsSold(item.id, Object.keys(saleOptions).length > 0 ? saleOptions : undefined);

      // The UI update for the item's status (e.g., SOLD badge, disabled buttons)
      // should ideally happen via the InventoryContext's real-time listener
      // or by the context refreshing its data after the modifyItem call triggered by markItemAsSold.
      // which would update the `item` prop of this modal.

      setIsSellModalOpen(false); // Close the sell confirmation modal.

      // Optionally, you might want to call `onClose()` for the main ItemDetailModal here
      // if the item becoming "SOLD" means the detail view is no longer relevant or should refresh.
      // For now, we assume the parent component or context handles the selectedItem state
      // and this modal will re-render with the updated `item` prop.

      if (showNotification) {
        showNotification(NotificationType.SUCCESS, 'Ürün başarıyla satıldı ve işlem kaydedildi.');
      }
    } catch (error: any) {
      console.error('Error during sale confirmation:', error);
      // Set error for the FormModal used for selling.
      setError(error.message || 'Ürün satılırken veya işlem kaydedilirken bir hata oluştu.');

      if (showNotification) {
        showNotification(NotificationType.ERROR, error.message || 'Satış işlemi sırasında bir hata oluştu.');
      }
    } finally {
      setIsSubmittingSell(false);
    }
  };

  // Handle delete operation
  const handleDelete = async () => {
    if (!item) return;

    try {
      setIsSubmitting(true);

      // Delete the item using context function
      await removeItem(item.id);

      // Close the modal
      onClose();

      if (showNotification) {
        showNotification(NotificationType.SUCCESS, 'Ürün başarıyla silindi.');
      }
    } catch (error) {
      setError('Ürün silinirken bir hata oluştu.');

      if (showNotification) {
        showNotification(NotificationType.ERROR, 'Ürün silinirken bir hata oluştu.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tab navigation rendering
  const renderTabNavigation = () => {
    const tabs = [
      { id: 'customer', label: 'Müşteri' },
      { id: 'details', label: 'Detaylar' },
      { id: 'edit', label: 'Düzenle' }
    ];

    // Don't show edit tab if the item is sold (can't edit sold items)
    const filteredTabs = isItemSold ? tabs.filter(tab => tab.id !== 'edit') : tabs;

    // Common action - Barcode Print
    const handleBarcodeButtonClick = () => {
      setTimeout(() => {
        const barcodeBtn = document.querySelector('.barcode-print-btn') as HTMLButtonElement;
        if (barcodeBtn) {
          barcodeBtn.click();
        }
      }, 0);
    };

    return (
      <div className="border-b border-gray-200">
        <div className="flex items-center justify-between">
          <nav className="-mb-px flex space-x-4 sm:space-x-8">
            {filteredTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id as ViewMode)}
                className={`
                  whitespace-nowrap py-4 px-1 font-medium text-sm sm:text-base border-b-2 
                  ${viewMode === tab.id 
                    ? 'border-primary-charcoal text-primary-charcoal' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            {/* Action buttons moved to header */}
            <Button
              onClick={handleBarcodeButtonClick}
              variant={ButtonVariant.SECONDARY}
              className="flex items-center"
              size={ButtonSize.SMALL}
              title="Barkod Yazdır"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </Button>

            {viewMode === 'customer' && !isItemSold && (
              <Button
                onClick={handleSellClick}
                variant={ButtonVariant.PRIMARY}
                className="flex items-center"
                size={ButtonSize.SMALL}
                title="Satış Yap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </Button>
            )}

            {viewMode === 'details' && onDelete && (
              <Button
                onClick={handleDelete}
                variant={ButtonVariant.DANGER}
                className="flex items-center"
                size={ButtonSize.SMALL}
                title="Sil"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Unified shared container for all workflows, now using DetailModal
  return (
    <>
      {/* Render barcode button in a portal outside all other modals */}
      {ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', zIndex: 9999 }}>
          <BarcodeButton
            barcode={item.barcode}
            item={item}
            buttonText=""
            className="barcode-print-btn"
          />
        </div>,
        document.body
      )}

      <div {...mobileSwipeHandlers}>
        <DetailModal
          isOpen={isOpen}
          onClose={onClose}
          title={
            viewMode === 'customer' 
              ? 'Ürün Fiyatı' 
              : viewMode === 'details' 
                ? 'Ürün Detayları' 
                : 'Ürün Düzenle'
          }
          actionButtons={[]} /* Hide the default action buttons */
          closeOnClickOutside={true}
          size="large"
        >
          {/* Tab Navigation Bar */}
          {renderTabNavigation()}

          {/* Content will change based on viewMode */}
          <div className="py-4 h-[650px] overflow-y-auto">
            {viewMode === 'customer' && (
              <div className="pb-12">
                <div className="p-6 bg-accent-blue text-primary-charcoal text-center">
                  <p className="text-sm uppercase tracking-wider mb-1 font-medium">Satış Fiyatı</p>
                  <p className="text-4xl font-bold">{formatItemPrice(item, 'sell')}</p>

                  {/* Show currency information for USD prices with tooltip */}
                  {item.sellCurrency === Currency.USD && (
                    <Tooltip text={`Kullanılan Kur: ${item.status === ItemStatus.SOLD && item.soldExchangeRateToTry ? item.soldExchangeRateToTry.toFixed(2) : currentExchangeRate.toFixed(2)} ₺/$`}>
                      <p className="text-lg mt-1">
                        ({formatPrice(tryEquivalent)})
                      </p>
                    </Tooltip>
                  )}

                  {/* Show weight for silver items */}
                  {isSilverItem && (
                    <p className="text-lg mt-2">
                      {formatWeight((item as SilverItem).weight)}
                    </p>
                  )}

                  {/* Display sold status if applicable */}
                  {isItemSold && (
                    <div className="mt-2 bg-red-600 text-white py-1 px-3 rounded-full text-xs font-bold inline-block">
                      Satıldı
                    </div>
                  )}

                  <h1 className="mt-2 text-xl font-semibold">{item.name}</h1>
                </div>

                <div className="p-4">
                  <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
                    <div className="bg-accent-white p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Ürün Kodu</p>
                      <p className="font-medium text-gray-900">{item.barcode}</p>
                    </div>

                    <div className="bg-accent-white p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Kategori</p>
                      <p className="font-medium text-gray-900">{categoryName}</p>
                    </div>

                    <div className="bg-accent-white p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Ürün Tipi</p>
                      <p className="font-medium text-gray-900">
                        {item.type === ItemType.SILVER ? 'Gümüş' : 'Saat'}
                      </p>
                    </div>

                    {isSilverItem && (
                      <>
                        <div className="bg-accent-white p-3 rounded-lg">
                          <p className="text-sm text-gray-500">Ağırlık</p>
                          <p className="font-medium text-gray-900">{formatWeight((item as SilverItem).weight)}</p>
                        </div>

                        <div className="bg-accent-white p-3 rounded-lg">
                          <p className="text-sm text-gray-500">Gram Fiyatı</p>
                          <p className="font-medium text-gray-900">
                            {formatCurrency((item as SilverItem).pricePerGram || 0, item.sellCurrency)}/gr
                          </p>
                        </div>
                      </>
                    )}

                    {isWatchItem && (
                      <div className="bg-accent-white p-3 rounded-lg">
                        <p className="text-sm text-gray-500">Marka</p>
                        <p className="font-medium text-gray-900">{(item as WatchItem).brand || '-'}</p>
                      </div>
                    )}

                    {/* Display currency information */}
                    <div className="bg-accent-white p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Para Birimi</p>
                      <p className="font-medium text-gray-900">
                        {item.sellCurrency === Currency.TRY ? 'TRY (₺)' : 'USD ($)'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Details View */}
            {viewMode === 'details' && (
              <div className="space-y-6 pb-12">
                <div className={`grid ${!isMobile ? 'grid-cols-2 gap-8' : 'grid-cols-1 gap-6'}`}>
                  <div>
                    <InfoGroup title="Temel Bilgiler">
                      <InfoField 
                        label="Ürün Tipi" 
                        value={item.type === ItemType.SILVER ? 'Gümüş' : 'Saat'} 
                      />
                      <InfoField 
                        label="Barkod" 
                        value={item.barcode} 
                      />
                      <InfoField 
                        label="Ürün Adı" 
                        value={item.name} 
                      />
                      <InfoField 
                        label="Kategori" 
                        value={categoryName} 
                      />
                      {item.purchaseDate && (
                        <InfoField 
                          label="Satın Alma Tarihi" 
                          value={formatDate(item.purchaseDate)} 
                        />
                      )}
                    </InfoGroup>

                    <InfoGroup title="Para Birimi Bilgileri" className="mt-6">
                      <InfoField 
                        label="Satış Para Birimi" 
                        value={item.sellCurrency === Currency.TRY ? 'Türk Lirası (₺)' : 'USD ($)'} 
                      />
                      <InfoField 
                        label="Maliyet Para Birimi" 
                        value={item.costCurrency === Currency.TRY ? 'Türk Lirası (₺)' : 'Amerikan Doları ($)'} 
                      />
                      {item.status === ItemStatus.SOLD && item.soldExchangeRateToTry && item.sellCurrency === Currency.USD && (
                        <InfoField 
                          label="Satış Anı Kur Oranı" 
                          value={`${item.soldExchangeRateToTry.toFixed(2)} ₺/$`} 
                        />
                      )}
                    </InfoGroup>
                  </div>

                  <div>
                    <InfoGroup title="Fiyat Bilgileri">
                      <InfoField 
                        label="Birim Satış Fiyatı" 
                        value={formatItemPrice(item, 'sell')} 
                      />
                      {item.sellCurrency === Currency.USD && (
                        <>
                          <InfoField 
                            label="TRY Karşılığı (Satış)" 
                            value={
                              <Tooltip text={`Kullanılan Kur: ${item.status === ItemStatus.SOLD && item.soldExchangeRateToTry ? item.soldExchangeRateToTry.toFixed(2) : currentExchangeRate.toFixed(2)} ₺/$`}>
                                <span>{formatPrice(tryEquivalent)}</span>
                              </Tooltip>
                            }
                          />
                          <InfoField 
                            label="Dönüşüm Kuru" 
                            value={`${item.status === ItemStatus.SOLD && item.soldExchangeRateToTry ? item.soldExchangeRateToTry.toFixed(2) : currentExchangeRate.toFixed(2)} ₺/$`} 
                          />
                        </>
                      )}
                      {item.costPerUnit > 0 && (
                        <>
                          <InfoField 
                            label="Birim Maliyet" 
                            value={formatItemPrice(item, 'cost')} 
                          />
                          {item.costCurrency === Currency.USD && item.costExchangeRateToTry && (
                            <InfoField 
                              label="Maliyet Kur Oranı (Alış)" 
                              value={`${item.costExchangeRateToTry.toFixed(2)} ₺/$`} 
                            />
                          )}
                        </>
                      )}
                      {isSilverItem && (
                        <>
                          <InfoField 
                            label="Ağırlık" 
                            value={formatWeight((item as SilverItem).weight)} 
                          />
                          <InfoField 
                            label="Gram Satış Fiyatı" 
                            value={`${formatCurrency((item as SilverItem).pricePerGram || 0, item.sellCurrency)}/gr`} 
                          />
                          {isSilverItemWithCost(item) && (
                            <InfoField 
                              label="Gram Maliyet" 
                              value={`${formatCurrency((item as SilverItem).costPerGram || 0, item.costCurrency)}/gr`} 
                            />
                          )}
                        </>
                      )}
                      {isWatchItem && (
                        <>
                          {(item as WatchItem).brand && <InfoField 
                            label="Marka" 
                            value={(item as WatchItem).brand || ''} 
                          />}
                        </>
                      )}
                    </InfoGroup>

                    <InfoGroup title="Sistem Bilgileri" className="mt-6">
                      <InfoField 
                        label="Oluşturulma Tarihi" 
                        value={formatDate(item.createdAt)} 
                      />
                      <InfoField 
                        label="Son Güncelleme" 
                        value={formatDate(item.updatedAt)} 
                      />
                    </InfoGroup>
                  </div>
                </div>
              </div>
            )}

            {/* Edit View */}
            {viewMode === 'edit' && (
              <form onSubmit={handleSubmit} className="space-y-4 pb-12">
                {/* Item Type (Display only, not editable) */}
                <FormField id="type" label="Ürün Tipi">
                  <div className="p-2 bg-gray-100 rounded-md">
                    {item.type === ItemType.SILVER ? 'Gümüş' : 'Saat'}
                  </div>
                </FormField>

                {/* Category */}
                <FormField id="category" label="Kategori" required>
                  <SelectInput
                    id="category"
                    name="category"
                    value={formData.category || ''}
                    onChange={handleInputChange}
                    options={categoryOptions}
                    placeholder="Kategori seçin"
                    required
                  />
                </FormField>

                {/* Name */}
                <FormField id="name" label="Ürün Adı" required>
                  <TextInput
                    id="name"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>

                {/* Barcode */}
                <FormField id="barcode" label="Barkod" required>
                  <TextInput
                    id="barcode"
                    name="barcode"
                    value={formData.barcode || ''}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>

                {/* Purchase Date */}
                <FormField id="purchaseDate" label="Satın Alma Tarihi">
                  <input
                    type="date"
                    id="purchaseDate"
                    name="purchaseDate"
                    value={formatDateForInput(formData.purchaseDate)}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </FormField>

                {/* Silver-specific fields */}
                {item.type === ItemType.SILVER ? (
                  <>
                    {/* Pricing mode selector for Silver */}
                    <FormField id="pricingMode" label="Fiyatlandırma Modu">
                      <div className="flex space-x-4">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            value="perGram"
                            checked={pricingMode === 'perGram'}
                            onChange={() => setPricingMode('perGram')}
                            className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                          />
                          <span className="ml-2 text-sm text-gray-700">Gram Fiyatı</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            value="perUnit"
                            checked={pricingMode === 'perUnit'}
                            onChange={() => setPricingMode('perUnit')}
                            className="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                          />
                          <span className="ml-2 text-sm text-gray-700">Birim Fiyat</span>
                        </label>
                      </div>
                    </FormField>

                    {/* Weight - Always show for Silver */}
                    <FormField id="weight" label="Ağırlık (g)" required>
                      <NumberInput
                        id="weight"
                        name="weight"
                        value={(formData as Partial<SilverItem>).weight || ''}
                        onChange={handleInputChange}
                        step="0.01"
                        min={0}
                        required
                      />
                    </FormField>

                    {pricingMode === 'perGram' ? (
                      <>
                        {/* Per Gram Pricing */}
                        <div className="grid grid-cols-2 gap-4">
                          <FormField id="pricePerGram" label="Gram Satış Fiyatı" required>
                            <NumberInput
                              id="pricePerGram"
                              name="pricePerGram"
                              value={(formData as Partial<SilverItem>).pricePerGram || ''}
                              onChange={handleInputChange}
                              step="0.01"
                              min={0}
                              required
                            />
                          </FormField>

                          <FormField id="sellCurrency" label="Para Birimi" required>
                            <SelectInput
                              id="sellCurrency"
                              name="sellCurrency"
                              value={formData.sellCurrency || Currency.TRY}
                              onChange={handleInputChange}
                              options={currencyOptions}
                            />
                          </FormField>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <FormField id="costPerGram" label="Gram Maliyet Fiyatı">
                            <NumberInput
                              id="costPerGram"
                              name="costPerGram"
                              value={(formData as Partial<SilverItem>).costPerGram || ''}
                              onChange={handleInputChange}
                              step="0.01"
                              min={0}
                            />
                          </FormField>

                          <FormField id="costCurrency" label="Para Birimi">
                            <SelectInput
                              id="costCurrency"
                              name="costCurrency"
                              value={formData.costCurrency || Currency.TRY}
                              onChange={handleInputChange}
                              options={currencyOptions}
                            />
                          </FormField>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Per Unit Pricing */}
                        <div className="grid grid-cols-2 gap-4">
                          <FormField id="sellPerUnit" label="Birim Satış Fiyatı" required>
                            <NumberInput
                              id="sellPerUnit"
                              name="sellPerUnit"
                              value={formData.sellPerUnit || ''}
                              onChange={handleInputChange}
                              step="0.01"
                              min={0}
                              required
                            />
                          </FormField>

                          <FormField id="sellCurrency" label="Para Birimi" required>
                            <SelectInput
                              id="sellCurrency"
                              name="sellCurrency"
                              value={formData.sellCurrency || Currency.TRY}
                              onChange={handleInputChange}
                              options={currencyOptions}
                            />
                          </FormField>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <FormField id="costPerUnit" label="Birim Maliyet Fiyatı">
                            <NumberInput
                              id="costPerUnit"
                              name="costPerUnit"
                              value={formData.costPerUnit || ''}
                              onChange={handleInputChange}
                              step="0.01"
                              min={0}
                            />
                          </FormField>

                          <FormField id="costCurrency" label="Para Birimi">
                            <SelectInput
                              id="costCurrency"
                              name="costCurrency"
                              value={formData.costCurrency || Currency.TRY}
                              onChange={handleInputChange}
                              options={currencyOptions}
                            />
                          </FormField>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {/* Watch-specific fields */}
                    <FormField id="brand" label="Marka">
                      <TextInput
                        id="brand"
                        name="brand"
                        value={(formData as Partial<WatchItem>).brand || ''}
                        onChange={handleInputChange}
                      />
                    </FormField>

                    {/* Pricing for Watch items */}
                    <div className="grid grid-cols-2 gap-4">
                      <FormField id="sellPerUnit" label="Satış Fiyatı" required>
                        <NumberInput
                          id="sellPerUnit"
                          name="sellPerUnit"
                          value={formData.sellPerUnit || ''}
                          onChange={handleInputChange}
                          step="0.01"
                          min={0}
                          required
                        />
                      </FormField>

                      <FormField id="sellCurrency" label="Para Birimi" required>
                        <SelectInput
                          id="sellCurrency"
                          name="sellCurrency"
                          value={formData.sellCurrency || Currency.TRY}
                          onChange={handleInputChange}
                          options={currencyOptions}
                        />
                      </FormField>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField id="costPerUnit" label="Maliyet Fiyatı">
                        <NumberInput
                          id="costPerUnit"
                          name="costPerUnit"
                          value={formData.costPerUnit || ''}
                          onChange={handleInputChange}
                          step="0.01"
                          min={0}
                        />
                      </FormField>

                      <FormField id="costCurrency" label="Para Birimi">
                        <SelectInput
                          id="costCurrency"
                          name="costCurrency"
                          value={formData.costCurrency || Currency.TRY}
                          onChange={handleInputChange}
                          options={currencyOptions}
                        />
                      </FormField>
                    </div>
                  </>
                )}

                {error && (
                  <div className="mt-2 text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* Edit mode buttons */}
                <div className="flex items-center justify-end gap-4 mt-6">
                  <Button
                    onClick={() => setViewMode('details')}
                    variant={ButtonVariant.SECONDARY}
                  >
                    İptal
                  </Button>

                  <Button
                    onClick={() => handleSubmit(new Event('submit') as unknown as React.FormEvent)}
                    variant={ButtonVariant.PRIMARY}
                    isLoading={isSubmitting}
                  >
                    {isSubmitting ? 'Güncelleniyor...' : 'Güncelle'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </DetailModal>

        {/* Sell confirmation modal - Replace with FormModal */}
        {ReactDOM.createPortal(
          <FormModal
            isOpen={isSellModalOpen}
            onClose={() => setIsSellModalOpen(false)}
            title="Satış Onayı"
            size="small"
            onSubmit={(e) => {
              e.preventDefault();
              handleSellConfirm();
            }}
            isSubmitting={isSubmittingSell}
            submitText="Satışı Tamamla"
            cancelText="İptal"
            error={error}
            hideButtons={false}
          >
            <div className="space-y-4">
              <p className="text-primary-charcoal">
                Bu ürünü satmak istediğinizden emin misiniz?
              </p>

              <div className="grid grid-cols-2 gap-4">
                <FormField 
                  id="sellPrice"
                  label="Satış Fiyatı"
                  required
                >
                  <NumberInput
                    id="sellPrice"
                    name="sellPrice"
                    value={sellPrice ?? ''}
                    onChange={handleSellPriceChange}
                    min={0}
                    step={0.01}
                    placeholder="Satış fiyatı"
                  />
                </FormField>

                <FormField 
                  id="sellCurrencySelect"
                  label="Para Birimi"
                  required
                >
                  <SelectInput
                    id="sellCurrencySelect"
                    name="sellCurrencySelect"
                    value={sellCurrency}
                    onChange={(e) => setSellCurrency(e.target.value as Currency)}
                    options={currencyOptions}
                  />
                </FormField>
              </div>

              <p className="mt-1 text-sm text-gray-500">
                Önceden belirlenmiş satış fiyatından farklı bir fiyata satılacaksa düzenleyebilirsiniz.
              </p>
            </div>
          </FormModal>,
          document.body
        )}
      </div>
    </>
  );
};

export default ItemDetailModal; 